import React from 'react';
import { OrderItemNote } from './order-item-note';
import { OrderItemQuantityBox } from './order-item-quantity-box';
import { OrderItemTopRow } from './orders/order-item-top-row';
import { SCREEN_PADDING_LARGE } from '@/styles';
import { StyleSheet, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props {
  description: string | null;
  footer?: React.ReactNode;
  formattedPrice: string | null;
  isFee?: boolean;
  notes?: string | null;
  onNotesPress?: (note: string) => void;
  quantity: number;
  subtitle: React.ReactNode | null;
  title: string;
}

export const OrderItemListItem: React.FC<Props> = ({
  description,
  footer,
  formattedPrice,
  isFee,
  notes,
  onNotesPress,
  quantity,
  subtitle,
  title,
}) => {
  const { isLargeLayout } = useLayoutContext();

  return (
    <>
      <View
        style={[
          isLargeLayout ? styles.mainContentLarge : styles.mainContent,
          isFee ? styles.mainContentCenter : null,
        ]}
      >
        {isLargeLayout ? (
          <>
            <View style={styles.quantityBoxWrapperLarge}>
              <OrderItemQuantityBox quantity={quantity} />
            </View>
            <View style={styles.titleDescriptionContainer}>
              <Typography numberOfLines={2} variant="paragraph-base-600">
                {title}
              </Typography>
              {description ? (
                <Typography numberOfLines={2} variant="paragraph-md">
                  {description}
                </Typography>
              ) : null}
              {footer}
            </View>
            {subtitle ? (
              <View style={styles.subtitleLarge}>{subtitle}</View>
            ) : null}
            <View style={styles.totalPriceLarge}>
              <Typography variant="paragraph-base">{formattedPrice}</Typography>
            </View>
          </>
        ) : (
          <>
            <View style={styles.quantityBoxWrapper}>
              <OrderItemQuantityBox quantity={quantity} />
            </View>
            <View style={styles.mainContentRight}>
              <OrderItemTopRow
                description={description}
                formattedPrice={formattedPrice}
                isFee={isFee}
                name={title}
              >
                {subtitle ? (
                  <View style={styles.subtitle}>{subtitle}</View>
                ) : null}
                {footer}
              </OrderItemTopRow>
            </View>
          </>
        )}
      </View>
      {notes && onNotesPress ? (
        <View style={styles.noteContainer}>
          <OrderItemNote
            notes={notes}
            onPress={() => onNotesPress(notes)}
            trackingData={{ name: 'order_item_list_item.note_button' }}
          />
        </View>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  mainContent: {
    flex: 1,
    flexDirection: 'row',
  },
  mainContentCenter: {
    alignItems: 'center',
  },
  mainContentLarge: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  mainContentRight: {
    flex: 1,
  },
  noteContainer: {
    marginTop: 8,
  },
  quantityBoxWrapper: {
    marginRight: 12,
  },
  quantityBoxWrapperLarge: {
    justifyContent: 'center',
    marginRight: 12,
  },
  subtitle: {
    flexShrink: 0,
  },
  subtitleLarge: {
    alignItems: 'center',
    flex: 2,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  titleDescriptionContainer: {
    flex: 3,
    justifyContent: 'center',
  },
  totalPriceLarge: {
    alignItems: 'flex-end',
    flex: 1,
    justifyContent: 'center',
  },
});
